import { Tracker, TrackerAttributes, TrackerOperationAttributes, } from './Tracker'
import { TrackerSetting, TrackerSettingAttributes, TrackerSettingOperationAttributes } from './TrackerSetting'
import GroupTrackerApiClient from '~/apiClients/GroupTrackerApiClient';
import DateHelper from '~/helpers/DateHelper';
import GroupHashHelper from '~/helpers/GroupHashHelper';

export interface GroupTrackerAttributes {
  id: number,
  trackerId: number,
  ndGroupHash: string,
  trackerSettingId: number,
  createdAt: string,
  updatedAt?: string,
  tracker: TrackerAttributes,
  trackerSetting: TrackerSettingAttributes,
}

export interface GroupTrackerCreationAttributes {
  GroupTracker: {
    ndGroupHash: string,
  },
  TrackerSetting: TrackerSettingOperationAttributes,
  Tracker: TrackerOperationAttributes,
}

export interface GroupTrackerUpdateAttributes {
  id: number,
  trackerSetting?: {
    clientId?: string,
    isLive?: boolean,
    defaultFieldValueType?: string,
    defaultFieldValueText?: string,
    extraFields?: Record<string, string> | null,
  },
  tracker?: {
    name: string,
    version: string,
    autoSync: number,
  },
}

export class GroupTracker {
  constructor(private readonly attributes: GroupTrackerAttributes) {
  }

  get id(): number {
    return this.attributes.id;
  }

  get lastModified(): string {
    const trackerDate = this.tracker.updatedAt ?? this.tracker.createdAt;
    const trackerSettingDate = this.trackerSetting.updatedAt ?? this.trackerSetting.createdAt;

    return new Date(trackerDate) > new Date(trackerSettingDate) ? DateHelper.format(trackerDate) : DateHelper.format(trackerSettingDate);
  }

  get tracker(): Tracker {
    return new Tracker(this.attributes.tracker);
  }

  get trackerSetting(): TrackerSetting {
    return new TrackerSetting(this.attributes.trackerSetting);
  }

  static async create(formData: { softwareId: number, name: string, version: string, type: number, clientId?: string, defaultFieldValueType: string, defaultFieldValueText: string, extraFields?: Record<string, string> }) {
    const { softwareId, clientId, defaultFieldValueType, defaultFieldValueText, extraFields, name, type, version } = formData;
    const apiClient = GroupTracker.getApiClient();

    return apiClient.createGroupTracker({
      GroupTracker: {
        ndGroupHash: GroupHashHelper.getGroupHash(),
      },
      TrackerSetting: {
        clientId,
        defaultFieldValueType,
        defaultFieldValueText,
        extraFields,
      },
      Tracker: {
        softwareId,
        name,
        type,
        version,
      },
    });
  }

  public async clone(allowRestoreToTemplate: boolean = false): Promise<GroupTracker> {
    return await GroupTracker.getApiClient().cloneGroupTracker(this.tracker.id, allowRestoreToTemplate);
  }

  static async findByTrackerId(trackerId: number, useHash: boolean = true) {
    return useHash ? await GroupTracker.getApiClient().provideGroupTracker(trackerId) : await GroupTracker.getApiClient().provideGroupTrackerById(trackerId);
  }

  async changeLiveStatus() {
    const attributes: GroupTrackerUpdateAttributes = {
      id: this.attributes.id,
      trackerSetting: {
        isLive: !this.attributes.trackerSetting.isLive,
      },
    }
    return await GroupTracker.getApiClient().updateGroupTracker(attributes);
  }

  async update(name: string, version: string, clientId: string = '', defaultFieldValueType: string = '', defaultFieldValueText: string = '', defaultAutoSync: number = 0, extraFields: Record<string, string> = {}) {
    const updatedAttributes: GroupTrackerUpdateAttributes = {
      id: this.attributes.id,
      trackerSetting: {
        clientId,
        defaultFieldValueType,
        defaultFieldValueText,
        extraFields,
      },
      tracker: {
        name,
        version,
        autoSync: defaultAutoSync,
      }
    };
    return await GroupTracker.getApiClient().updateGroupTracker(updatedAttributes);
  }

  private static getApiClient(): GroupTrackerApiClient {
    return new GroupTrackerApiClient();
  }
}
